/**
 * Data-provider Refine поверх admin-эндпоинтов backend'а.
 *
 * Готовый REST-провайдер не подошёл: он ожидает ответы вида `[...]` плюс
 * заголовок `x-total-count`, а наш API отдаёт `{ photos: [...], page: { total } }` —
 * форму, выведенную из .proto. Подгонять контракт под конвенции библиотеки
 * было бы хвостом, виляющим собакой, поэтому провайдер написан руками.
 */
import type { CrudFilter, CrudSort, DataProvider } from "@refinedev/core";

import { API_URL, request } from "../lib/http";

/** Как ресурс Refine ложится на эндпоинт и на имя массива в ответе. */
const RESOURCES: Record<string, { path: string; collection: string }> = {
  photos: { path: "/api/v1/admin/photos", collection: "photos" },
  quests: { path: "/api/v1/admin/quests", collection: "quests" },
  users: { path: "/api/v1/admin/users", collection: "users" },
  tags: { path: "/api/v1/admin/tags", collection: "tags" },
  feedback: { path: "/api/v1/admin/feedback", collection: "items" },
  announcements: { path: "/api/v1/admin/announcements", collection: "announcements" },
  "landing-photos": { path: "/api/v1/admin/landing-photos", collection: "photos" },
};

/**
 * Refine адресует записи полем id, а у тега ключ — код (slug). Подставляем
 * его, иначе таблица не отличит строки друг от друга, а правка и удаление
 * уйдут в никуда.
 */
function withId(resource: string, item: unknown): unknown {
  if (resource !== "tags" || typeof item !== "object" || item === null) return item;
  const tag = item as { slug?: string };
  return { ...tag, id: tag.slug };
}

function resolve(resource: string) {
  const config = RESOURCES[resource];
  if (!config) {
    throw new Error(`Неизвестный ресурс: ${resource}`);
  }
  return config;
}

/**
 * Сортировка по дате добавления. Контракт умеет только её и только у фото и
 * квестов; другим ресурсам параметр не передаётся вовсе — сервер отверг бы
 * незнакомое поле запроса.
 */
function createdOrder(resource: string, sorters?: CrudSort[]): Record<string, string> {
  if (resource !== "photos" && resource !== "quests") return {};
  const created = sorters?.find((sorter) => sorter.field === "createdAt");
  if (!created) return {};
  return {
    order: created.order === "asc" ? "ADMIN_CREATED_ORDER_OLDEST_FIRST" : "ADMIN_CREATED_ORDER_NEWEST_FIRST",
  };
}

/** Плоские фильтры Refine превращаются в query-параметры нашего API. */
function toQuery(filters?: CrudFilter[]): Record<string, string> {
  const query: Record<string, string> = {};
  if (!filters) return query;

  for (const filter of filters) {
    if (!("field" in filter) || filter.value === undefined || filter.value === "") continue;

    switch (filter.field) {
      case "search":
      case "q":
        query.search = String(filter.value);
        break;
      case "status":
      case "type":
      case "role":
        query[filter.field] = String(filter.value);
        break;
      default:
        // Остальные поля backend не фильтрует — молча игнорируем,
        // чтобы случайный фильтр в UI не ломал запрос.
        break;
    }
  }
  return query;
}

interface ListResponse {
  page?: { total?: string | number };
  [key: string]: unknown;
}

export const dataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  getList: async ({ resource, pagination, filters, sorters }) => {
    const { path, collection } = resolve(resource);

    // В разных версиях Refine поле называлось current и currentPage —
    // читаем оба, чтобы провайдер не ломался при обновлении библиотеки.
    const paging = pagination as { currentPage?: number; current?: number; pageSize?: number };
    const current = paging?.currentPage ?? paging?.current ?? 1;
    const pageSize = paging?.pageSize ?? 25;

    const response = await request<ListResponse>(path, {
      query: {
        "page.page": current,
        "page.pageSize": pageSize,
        ...toQuery(filters),
        ...createdOrder(resource, sorters),
      },
    });

    const items = ((response[collection] as unknown[]) ?? []).map((item) => withId(resource, item));
    // total приходит строкой: int64 в JSON из protobuf сериализуется как строка.
    const total = Number(response.page?.total ?? items.length);

    return { data: items as never, total };
  },

  getOne: async ({ resource, id }) => {
    const { path } = resolve(resource);
    const data = await request(`${path}/${id}`);
    return { data: withId(resource, data) as never };
  },

  create: async ({ resource, variables }) => {
    const { path } = resolve(resource);
    const data = await request(path, { method: "POST", body: variables });
    return { data: withId(resource, data) as never };
  },

  update: async ({ resource, id, variables }) => {
    const { path } = resolve(resource);

    // У пользователей меняется только роль, и для неё отдельный эндпоинт:
    // остальные поля профиля админ править не может.
    if (resource === "users") {
      const data = await request(`${path}/${id}/role`, {
        method: "PUT",
        body: { id, role: (variables as { role?: string }).role },
      });
      return { data: data as never };
    }

    const data = await request(`${path}/${id}`, {
      method: "PUT",
      body: { ...(variables as object), id },
    });
    return { data: data as never };
  },

  deleteOne: async ({ resource, id }) => {
    const { path } = resolve(resource);
    await request(`${path}/${id}`, { method: "DELETE" });
    return { data: { id } as never };
  },

  // getMany без серверного batch-эндпоинта: запрашиваем по одному.
  // Списки в админке короткие, поэтому лишний round-trip некритичен.
  getMany: async ({ resource, ids }) => {
    const { path } = resolve(resource);
    const data = await Promise.all(ids.map((id) => request(`${path}/${id}`)));
    return { data: data as never };
  },

  custom: async ({ url, method, payload }) => {
    const data = await request(url, {
      method: (method?.toUpperCase() as "GET" | "POST" | "PUT" | "DELETE") ?? "GET",
      body: payload,
    });
    return { data: data as never };
  },
};
